import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useDarkMode } from "../../contexts/DarkModeContext";
import { FaArrowLeft, FaEye, FaUser, FaGraduationCap, FaBook } from "react-icons/fa";
import { toast } from "react-toastify";
import { fetchDutyRecords, clearDutyRecords, clearDutyRecordsError, setCurrentPage } from "../../redux/slices/dutyRecordsSlice";
import styles from "./DutyRecords.module.css";

function DutyRecords() {
  const { isDarkMode } = useDarkMode();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  

  
  const { students, pagination, loading, error, currentPage } = useSelector((state) => state.dutyRecords);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Check if id is valid
    if (!id || id === 'undefined') {
      toast.error('معرف الحصة غير صحيح');
      navigate('/');
      return;
    }
    
    dispatch(clearDutyRecords());
    dispatch(fetchDutyRecords({ lessonId: id, page: currentPage }));
  }, [dispatch, id, currentPage, navigate]);

  // Handle errors
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearDutyRecordsError());
    }
  }, [error, dispatch]);

  const handleViewDetails = (studentId) => {
    navigate(`/duty-details/${id}?student_id=${studentId}`);
  };

  const handlePageChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  const calculatePercentage = (studentDegree, totalDegree) => {
    if (totalDegree === 0) return 0;
    return Math.round((studentDegree / totalDegree) * 100);
  };

  if (loading) {
    return (
      <div className={`duty-records-page ${isDarkMode ? "dark-mode" : "light-mode"}`}>
        <div className="container">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">جاري التحميل...</span>
            </div>
            <p className="mt-3">جاري تحميل سجل الواجبات...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles['duty-records-page']} ${isDarkMode ? styles['dark-mode'] : styles['light-mode']}`}>
      <div className="container">
        {/* Header */}
        <div className={styles['duty-records-header']}>
          <div className="d-flex gap-2">
            <button 
              className={styles['back-btn']}
              onClick={() => navigate(`/lessonDetails/${id}`)}
            >
              <FaArrowLeft />
              رجوع لتفاصيل الحصة
            </button>
          </div>
          
          <div className={styles['duty-records-title']}>
            <h1>سجل الواجبات</h1>
            <p>عرض قائمة الطلاب الذين أدخلوا الواجب</p>
          </div>
        </div>

        {/* Statistics */}
        <div className={styles['duty-records-stats']}>
          <div className={styles['stats-card']}>
            <div className={styles['stats-icon']}>
              <FaUser />
            </div>
            <div className={styles['stats-content']}>
              <h3>{pagination.total}</h3>
              <p>إجمالي الطلاب</p>
            </div>
          </div>
          
          <div className={styles['stats-card']}>
            <div className={styles['stats-icon']}>
              <FaBook />
            </div>
            <div className={styles['stats-content']}>
              <h3>{students.length}</h3>
              <p>الطلاب في هذه الصفحة</p>
            </div>
          </div>
        </div>

        {/* Students List */}
        <div className={styles['duty-records-content']}>
          {students.length > 0 ? (
            <div className={styles['students-list']}>
              {students.map((student) => (
                <div key={student.id} className={styles['student-card']}>
                  <div className={styles['student-info']}>
                    <div className={styles['student-avatar']}>
                      {student.image ? (
                        <img src={student.image} alt={student.name} />
                      ) : (
                        <div className={styles['avatar-placeholder']}>
                          <FaUser />
                        </div>
                      )}
                    </div>
                    
                    <div className={styles['student-details']}>
                      <h4 className={styles['student-name']}>{student.name}</h4>
                      <div className={styles['student-meta']}>
                        <span className={styles['subject']}>
                          <FaBook className="me-1" />
                          {student.subject_name}
                        </span>
                        <span className={styles['class']}>
                          <FaGraduationCap className="me-1" />
                          {student.class_name}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className={styles['student-score']}>
                    <div className={styles['score-info']}>
                      <span className={styles['score-value']}>
                        {student.student_degree}/{student.total_degree}
                      </span>
                      <span className={styles['score-percentage']}>
                        {calculatePercentage(student.student_degree, student.total_degree)}%
                      </span>
                    </div>
                    
                    <div className={styles['score-bar']}>
                      <div 
                        className={styles['score-fill']}
                        style={{ 
                          width: `${calculatePercentage(student.student_degree, student.total_degree)}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                  
                  <div className={styles['student-actions']}>
                    <button
                      className={styles['view-details-btn']}
                      onClick={() => handleViewDetails(student.id)}
                    >
                      <FaEye />
                      عرض التفاصيل
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles['empty-state']}>
              <div className={styles['empty-icon']}>
                <FaBook />
              </div>
              <h3>لا يوجد طلاب</h3>
              <p>لم يقم أي طالب بإدخال الواجب بعد</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.pages_count > 1 && (
          <div className={styles['pagination-container']}>
            <nav aria-label="صفحات سجل الواجبات">
              <ul className={styles['pagination']}>
                <li className={`${styles['page-item']} ${currentPage === 1 ? styles['disabled'] : ''}`}>
                  <button
                    className={styles['page-link']}
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    السابق
                  </button>
                </li>
                
                {Array.from({ length: pagination.pages_count }, (_, i) => i + 1).map((page) => (
                  <li key={page} className={`${styles['page-item']} ${currentPage === page ? styles['active'] : ''}`}>
                    <button
                      className={styles['page-link']}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  </li>
                ))}
                
                <li className={`${styles['page-item']} ${currentPage === pagination.pages_count ? styles['disabled'] : ''}`}>
                  <button
                    className={styles['page-link']}
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === pagination.pages_count}
                  >
                    التالي
                  </button>
                </li>
              </ul>
            </nav>
            
            <div className={styles['pagination-info']}>
              <span>
                عرض {((currentPage - 1) * pagination.per_page) + 1} إلى {Math.min(currentPage * pagination.per_page, pagination.total)} من {pagination.total} طالب
              </span>
            </div>
          </div>
        )}

        {/* Navigation Actions */}
        <div className="d-flex gap-3 justify-content-center mt-4">
          <button
            className="btn btn-primary"
            onClick={() => navigate(`/lessonDetails/${id}`)}
          >
            العودة لتفاصيل الحصة
          </button>
          
          <button
            className="btn btn-secondary"
            onClick={() => navigate(`/create-duty/${id}`)}
          >
            إنشاء واجب جديد
          </button>
        </div>
      </div>
    </div>
  );
}

export default DutyRecords;
